import {
  ProjectSharingProvider,
  createBrowserProjectSharingServices,
} from '@/projectSharing/services';
import ProjectSharePage from '@/projectSharing/ProjectSharePage';
import { EditorRoute } from '@/routes/EditorRoute';
import '@/App.css';
import { AccountProfileScopeProvider } from '@/account/AccountProfileScope';
import { DiscoverySessionProvider } from '@/discovery/DiscoverySession';
import { loadBundledExtensions } from '@/extensions/bundled';
import { extensionPackages } from '@/extensions/packages/store';
import { lazy, Suspense, useEffect, useState } from 'react';

import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { AppModel, AppModelContext } from '@/models/AppModel';
import { ErrorPage } from '@/routes/ErrorPage';
import { StartPage } from '@/routes/StartPage';
import { UiModel } from '@/models/UiModel';
import { ChipStoreModel } from '@/models/ChipStoreModel';
import { PixiManagerModel } from '@/models/PixiManagerModel';
import { UserPreferencesModel } from '@/models/UserPreferencesModel';
import { KeyMapModel } from '@/models/KeyMapModel';
import {
  ChipEditorModelFactory,
  ChipInstanceModelFactory,
  ChipModelFactory,
  ChipSelectModelFactory,
  CircuitEditorModelFactory,
  CircuitModelFactory,
  ConnectionModelFactory,
  EditorModelFactory,
  EventBusFactory,
  LogicCircuitModelFactory,
  LogicGraphFactory,
  ProjectModelFactory,
  StructureCircuitModelFactory,
  TileManagerFactory,
  TileSelectModelFactory,
} from '@/factories';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { installDebugExport } from '@/utils/debugExport';
import { IndexedDbStorageProvider } from '@/storage/IndexedDbStorageProvider';
import { CurrentPersistenceService } from '@/services/CurrentPersistenceService';
import { EditorResourcesBoundary } from '@/components/loading/EditorResourcesBoundary';

loadBundledExtensions();

const TutorialPage = lazy(() => import('@/tutorial/TutorialPage'));
const ExtensionsPage = lazy(() =>
  import('@/routes/ExtensionsPage').then(({ ExtensionsPage }) => ({
    default: ExtensionsPage,
  })),
);
const SandboxPage = lazy(() =>
  import('@/routes/SandboxPage').then(({ SandboxPage }) => ({ default: SandboxPage })),
);
const ChallengesPage = lazy(() => import('@/challenges/ChallengesPage'));
const DailyPage = lazy(() => import('@/dailyChallenges/DailyPage'));
const ChallengeShareEntry = lazy(() => import('@/challenges/ChallengeShareEntry'));
const loadChallengeEditor = () => import('@/challenges/ChallengeEditor');
const ChallengeEditor = lazy(loadChallengeEditor);
const challengeRoute = (editor = false) => (
  <Suspense fallback={<main role="status">Opening challenges…</main>}>
    {editor ? (
      <EditorResourcesBoundary prepareModule={loadChallengeEditor}>
        <ChallengeEditor />
      </EditorResourcesBoundary>
    ) : (
      <ChallengesPage />
    )}
  </Suspense>
);
const tutorialRoute = (
  <Suspense
    fallback={
      <main
        role="status"
        css={{
          minHeight: '100%',
          padding: 32,
          background: '#1c2127',
          color: '#f0f3f6',
        }}
      >
        Opening lessons…
      </main>
    }
  >
    <TutorialPage />
  </Suspense>
);

// A hash router is used here such that the app can be deployed to GitHub pages
// depending on your deployment strategy, you may want to use a different router
export const router = createBrowserRouter([
  {
    path: '/daily',
    element: (
      <Suspense fallback={<main role="status">Opening daily challenge…</main>}>
        <DailyPage />
      </Suspense>
    ),
  },
  { path: '/share/:id', element: <ProjectSharePage /> },
  { path: '/challenges', element: challengeRoute() },
  {
    path: '/challenges/share',
    element: (
      <Suspense fallback={<main role="status">Opening challenge…</main>}>
        <ChallengeShareEntry />
      </Suspense>
    ),
  },
  { path: '/challenges/new', element: challengeRoute(true) },
  { path: '/challenges/edit/:id', element: challengeRoute(true) },
  { path: '/tutorial', element: tutorialRoute },
  { path: '/tutorial/:lessonId', element: tutorialRoute },
  {
    path: '/extensions',
    element: (
      <Suspense fallback={<main role="status">Opening extensions…</main>}>
        <ExtensionsPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <StartPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/editor',
    element: <EditorRoute />,
  },
  {
    path: '/sandbox',
    element: (
      <Suspense fallback={<main role="status">Opening sandbox…</main>}>
        <SandboxPage />
      </Suspense>
    ),
  },
]);

export const uiModel = new UiModel();
export const keyMapModel = new KeyMapModel();
export const userPreferencesModel = new UserPreferencesModel({ keyMapModel });
export const pixiManagerModel = new PixiManagerModel();

const tileManagerFactory = new TileManagerFactory();
const connectionModelFactory = new ConnectionModelFactory(
  tileDefinitionRegistry.get.bind(tileDefinitionRegistry),
);
const circuitModelFactory = new CircuitModelFactory(
  tileManagerFactory,
  connectionModelFactory,
);
const logicCircuitModelFactory = new LogicCircuitModelFactory(true);
const structureCircuitModelFactory = new StructureCircuitModelFactory();
const logicGraphFactory = new LogicGraphFactory();
const chipInstanceModelFactory = new ChipInstanceModelFactory(logicGraphFactory);
const chipModelFactory = new ChipModelFactory(
  circuitModelFactory,
  logicCircuitModelFactory,
  structureCircuitModelFactory,
  chipInstanceModelFactory,
);

const circuitEditorModelFactory = new CircuitEditorModelFactory();
const chipEditorModelFactory = new ChipEditorModelFactory(circuitEditorModelFactory);
export const chipStoreModel = new ChipStoreModel((chip) =>
  chipEditorModelFactory.create(chip),
);

const tileSelectModelFactory = new TileSelectModelFactory(
  tileManagerFactory,
  connectionModelFactory,
);
const chipSelectModelFactory = new ChipSelectModelFactory(tileManagerFactory);
const editorModelFactory = new EditorModelFactory(
  tileSelectModelFactory,
  chipSelectModelFactory,
);
const projectModelFactory = new ProjectModelFactory();
const eventBusFactory = new EventBusFactory();

const storageProvider = new IndexedDbStorageProvider('digitiles', undefined, {
  preserveLegacyProjects: true,
});
const persistenceService = new CurrentPersistenceService(storageProvider);

export const appModel = new AppModel(
  uiModel,
  chipStoreModel,
  pixiManagerModel,
  userPreferencesModel,
  chipModelFactory,
  editorModelFactory,
  projectModelFactory,
  eventBusFactory,
  persistenceService,
  { deferGraphics: true },
);

installDebugExport(appModel);

export function App() {
  const [sharingServices] = useState(createBrowserProjectSharingServices);
  useEffect(() => {
    if (import.meta.env.VITE_ACCOUNT_MODE !== 'verified-auth-v1') return;
    let disposed = false;
    let stop: (() => void) | undefined;
    void import('@/account/runtime')
      .then(({ startAccounts }) => {
        if (!disposed) stop = startAccounts();
      })
      .catch(() => {
        /* Optional accounts must not block local play. */
      });
    return () => {
      disposed = true;
      stop?.();
    };
  }, []);
  const [extensionsReady, setExtensionsReady] = useState(extensionPackages.ready);
  useEffect(() => {
    let mounted = true;
    extensionPackages
      .initialize(new URLSearchParams(window.location.search).get('extensions') === 'off')
      .finally(() => {
        if (mounted) setExtensionsReady(true);
      });
    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    const click = (event: MouseEvent) => {
      const button =
        event.target instanceof Element
          ? event.target.closest('button, [role="button"]')
          : null;
      if (
        button &&
        !button.hasAttribute('disabled') &&
        button.getAttribute('aria-disabled') !== 'true'
      )
        appModel.playSound('click');
    };
    document.addEventListener('click', click);
    return () => document.removeEventListener('click', click);
  }, []);
  return (
    <AppModelContext.Provider value={appModel}>
      <div className="app-viewport">
        <div className="app-content">
          {extensionsReady ? (
            <AccountProfileScopeProvider>
              <DiscoverySessionProvider>
                <ProjectSharingProvider services={sharingServices}>
                  <RouterProvider router={router} />
                </ProjectSharingProvider>
              </DiscoverySessionProvider>
            </AccountProfileScopeProvider>
          ) : (
            <main
              aria-label="Loading extensions"
              role="status"
              css={{
                minHeight: '100%',
                background: '#1c2127',
                color: '#f0f3f6',
                padding: 24,
              }}
            >
              Loading enabled extensions…
            </main>
          )}
        </div>
      </div>
    </AppModelContext.Provider>
  );
}
